import { z } from 'zod';
/**
 * The one pagination shape for every `/api/v1` list endpoint (KIR-4376).
 *
 * Defined once and reused rather than restated per resource: the parent story
 * makes the public field names a contract, and two resources that paginate with
 * *almost* the same field names is the kind of inconsistency a customer's
 * generic client cannot absorb.
 *
 * The names deliberately differ from the internal table contract
 * (`limit` / `total` / `hasMore`). `pageSize` says what it is without the reader
 * having to guess whether `limit` caps the page or the whole result set, and
 * `totalItems` / `totalPages` sit next to each other so neither is mistaken for
 * the other. The DTO layer absorbs the rename.
 */
/** Page size used when the caller does not ask for one. */
export const V1_DEFAULT_PAGE_SIZE = 25;
/**
 * Hard ceiling on `pageSize`. A request above it is a 400 rather than a silent
 * clamp — a SIEM connector asking for 1000 and receiving 100 without being told
 * will happily believe it has read everything.
 */
export const V1_MAX_PAGE_SIZE = 100;
/**
 * Query fragment every list endpoint extends.
 *
 * Coerced because these arrive as query-string text. Both fields are optional
 * with defaults, so a caller that sends neither still gets a well-defined page.
 */
export const v1PaginationQuerySchema = z.object({
    page: z.coerce
        .number()
        .int()
        .positive()
        .default(1)
        .describe('1-based page number.')
        // `z.coerce.number()` accepts the query string's text, so on the *input*
        // side of the generated schema it is `unknown` and would publish as an
        // untyped parameter. Stating the rendered type keeps the documented
        // parameter as specific as the validation actually is.
        .meta({ type: 'integer', minimum: 1, examples: [1] }),
    pageSize: z.coerce
        .number()
        .int()
        .positive()
        .max(V1_MAX_PAGE_SIZE)
        .default(V1_DEFAULT_PAGE_SIZE)
        .describe(`Rows per page, ${V1_DEFAULT_PAGE_SIZE} by default. Asking for more than ${V1_MAX_PAGE_SIZE} is a 400 rather than a silent clamp, so you are never told you have read everything when you have not.`)
        .meta({
        type: 'integer',
        minimum: 1,
        maximum: V1_MAX_PAGE_SIZE,
        examples: [V1_DEFAULT_PAGE_SIZE],
    }),
});
/**
 * The `pagination` object on every list response.
 *
 * `hasMore` is redundant with `page < totalPages` and is included anyway: it is
 * the single field a paging loop needs, and computing it here means every
 * client computes it the same way.
 */
export const v1PaginationSchema = z
    .object({
    page: z.number().int().positive().describe('The page this response is.'),
    pageSize: z
        .number()
        .int()
        .positive()
        .describe('Rows per page in force for this response.'),
    totalItems: z
        .number()
        .int()
        .min(0)
        .describe('Total rows matching the filters, across every page.'),
    totalPages: z
        .number()
        .int()
        .min(0)
        .describe('Total pages at this `pageSize`.'),
    hasMore: z
        .boolean()
        .describe('Whether another page follows. Equivalent to `page < totalPages`, and included so every client computes it the same way.'),
})
    .meta({
    id: 'V1Pagination',
    title: 'Pagination',
    description: 'Where this page sits in the full result set. The same shape on every list endpoint.',
    examples: [
        { page: 1, pageSize: 25, totalItems: 137, totalPages: 6, hasMore: true },
    ],
});
/**
 * Build the response fragment from what the handler already knows.
 *
 * Shared so `totalPages` rounding and the `hasMore` predicate cannot drift
 * between resources — the exact drift the "consistent across both resources"
 * requirement is about.
 */
export function buildV1Pagination(page, pageSize, totalItems) {
    const totalPages = pageSize > 0 ? Math.ceil(totalItems / pageSize) : 0;
    return {
        page,
        pageSize,
        totalItems,
        totalPages,
        hasMore: page * pageSize < totalItems,
    };
}
//# sourceMappingURL=pagination.js.map